/**
 * `/tenants/new` — provision a workspace.
 *
 * One submit creates three things on the API side, in a single transaction: the
 * tenant, its feature flags, and its owner *with no password*. The API then
 * emails the owner a single-use, 24-hour set-password link.
 *
 * SECURITY, and the reason this screen is written the way it is: there is no
 * password on this form and none in the response. `createTenantResponseSchema`
 * carries the link's expiry and nothing else, so this screen cannot display or
 * imply a password even by accident — it can only say that the email went out.
 *
 * Validation is `createTenantRequestSchema` from `@wend/shared`, the same schema
 * the API parses the body with. Server-side `fieldErrors` (a duplicate owner
 * email, a subscription key already in use) land on the same fields.
 *
 * THE BLUEPRINT LOOK (slice 66), visual only. The features are chosen with
 * `FeaturePicker` — the same toggle chips the correction form uses, with each
 * part's one-line description kept under them (that file says why the lines
 * stayed). The success screen's "email sent" box is the product's `Callout`
 * in its good tone rather than a hand-drawn green box, the summary's labels
 * are kickers, and "Created" is `@wend/ui`'s Badge — the local `Pill` it used
 * was the same classes under another name and is gone. The default feature
 * set and every handler are unchanged.
 */
import { ArrowLeft, CircleCheck } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { createTenantRequestSchema, type CreateTenantResponse, type Feature } from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Kicker,
  buttonVariants,
  useToast,
} from '@wend/ui';

import { AdminShell } from '../components/AdminShell';
import { FormAlert } from '../components/FormAlert';
import { FeaturePicker } from '../components/FeaturePicker';
import { FeatureTags } from '../components/FeatureTags';
import { describeApiError, toApiError, type ApiError } from '../lib/api-error';
import { toIsoEndOfDay, todayInputValue } from '../lib/date-input';
import { formatDate, formatDateTime, formatRelative } from '../lib/format';
import {
  NO_FIELD_ERRORS,
  toFieldErrorMap,
  zodFieldErrors,
  type FieldErrorMap,
} from '../lib/form';
import { createTenant } from '../lib/tenants-api';

/* -------------------------------------------------------------------------- */
/* form state                                                                  */
/* -------------------------------------------------------------------------- */

interface TenantFormState {
  name: string;
  email: string;
  phone: string;
  legalName: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  taxId: string;
  subscriptionKey: string;
  /** `yyyy-mm-dd` from the date input, converted on submit. */
  validTill: string;
  features: Feature[];
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
}

const EMPTY_FORM: TenantFormState = {
  name: '',
  email: '',
  phone: '',
  legalName: '',
  website: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  taxId: '',
  subscriptionKey: '',
  validTill: '',
  /*
   * Reach and Connect, which is what `['reach']` meant until slice 65 split the
   * old Reach into paid ads (Reach) and email/WhatsApp campaigns (Connect).
   * Keeping the old default's meaning means a workspace provisioned the way
   * staff always have still gets campaigns; dropping Connect here would hand
   * new workspaces an empty Overview and no Campaigns screen without anybody
   * having decided that.
   */
  features: ['reach', 'connect'],
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
};

/**
 * Where zod would say "Invalid input" — the union in `isoDateTimeSchema` cannot
 * describe itself usefully — say something a person can act on. Only applied to
 * zod's own generic messages; see `toFieldErrorMap`.
 */
const FRIENDLY_MESSAGES: Readonly<Record<string, string>> = {
  validTill: 'Choose the date the subscription expires.',
  features: 'Enable at least one feature.',
};

/** An empty optional field is absent, not an empty string. */
function optionalText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/*
  `toIsoEndOfDay` and `todayInputValue` used to live here, when this was the only
  screen in the admin panel with a date on it. Slice 8 added a second screen that
  writes the same `valid_till` column, so they moved to `lib/date-input.ts` — two
  implementations of "the end of that day" would eventually disagree, and the way
  they would disagree is that a subscription set from one screen would expire a
  few hours earlier than the identical one set from the other.
*/

/** The request body, before `createTenantRequestSchema` has had its say. */
function buildRequest(form: TenantFormState): unknown {
  return {
    name: form.name,
    email: form.email,
    phone: form.phone,
    organisation: {
      legalName: form.legalName,
      website: optionalText(form.website),
      addressLine1: optionalText(form.addressLine1),
      addressLine2: optionalText(form.addressLine2),
      city: optionalText(form.city),
      state: optionalText(form.state),
      postalCode: optionalText(form.postalCode),
      country: optionalText(form.country),
      taxId: optionalText(form.taxId),
    },
    subscriptionKey: form.subscriptionKey,
    validTill: toIsoEndOfDay(form.validTill),
    features: form.features,
    owner: {
      name: form.ownerName,
      email: form.ownerEmail,
      phone: optionalText(form.ownerPhone),
    },
  };
}

/* -------------------------------------------------------------------------- */
/* small local pieces                                                          */
/* -------------------------------------------------------------------------- */

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  description?: string;
  required?: boolean;
  type?: 'text' | 'email' | 'tel' | 'url' | 'date';
  placeholder?: string;
  autoComplete?: string;
  min?: string;
  className?: string;
}

/** `FormField` + `Input` from @wend/ui, wired to a controlled string. */
function TextField({
  label,
  value,
  onChange,
  error,
  description,
  required = false,
  type = 'text',
  placeholder,
  autoComplete,
  min,
  className,
}: TextFieldProps) {
  return (
    <FormField label={label} error={error} description={description} required={required} className={className}>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        min={min}
        spellCheck={type === 'email' || type === 'url' ? false : undefined}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </FormField>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1.02rem]">{title}</CardTitle>
        {description !== undefined && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-5">{children}</CardContent>
    </Card>
  );
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b border-line py-3 last:border-b-0 sm:flex-row sm:gap-4">
      <Kicker as="dt" className="leading-6 sm:w-44 sm:shrink-0">
        {label}
      </Kicker>
      <dd className="min-w-0 text-[0.9rem] leading-6">{children}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* screen                                                                      */
/* -------------------------------------------------------------------------- */

export function NewTenantPage() {
  const { toast } = useToast();

  const [form, setForm] = useState<TenantFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrorMap>(NO_FIELD_ERRORS);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [localMessage, setLocalMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CreateTenantResponse | null>(null);

  const update = (patch: Partial<TenantFormState>): void => {
    setForm((current) => ({ ...current, ...patch }));
  };

  // `FeaturePicker` hands back the set already rebuilt in display order.
  const setFeatures = (features: Feature[]): void => {
    setForm((current) => ({ ...current, features }));
  };

  const reset = (): void => {
    setForm(EMPTY_FORM);
    setErrors(NO_FIELD_ERRORS);
    setFailure(null);
    setLocalMessage(null);
    setCreated(null);
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    setLocalMessage(null);

    const parsed = createTenantRequestSchema.safeParse(buildRequest(form));
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error, FRIENDLY_MESSAGES));
      setLocalMessage('Some details need attention. Check the highlighted fields.');
      return;
    }

    setErrors(NO_FIELD_ERRORS);
    setSubmitting(true);

    try {
      const result = await createTenant(parsed.data);
      setCreated(result);
      toast({
        tone: 'success',
        title: 'Workspace created',
        description: `Set-password link emailed to ${result.owner.email}.`,
      });
    } catch (error: unknown) {
      const apiError = toApiError(error);
      setFailure(apiError);
      setErrors(toFieldErrorMap(apiError.fieldErrors));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit();
  };

  const backLink = (
    <Link
      to="/tenants"
      className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted transition-colors hover:text-text"
    >
      <ArrowLeft aria-hidden="true" className="size-3.5" />
      Tenants
    </Link>
  );

  if (created !== null) {
    return (
      <AdminShell title="Workspace created">
        <div className="flex max-w-3xl flex-col gap-5">
          {backLink}
          <CreatedPanel result={created} onCreateAnother={reset} />
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="New tenant">
      <form onSubmit={onSubmit} noValidate className="flex max-w-3xl flex-col gap-5">
        {backLink}

        {failure !== null && (
          <FormAlert requestId={failure.requestId}>{describeApiError(failure)}</FormAlert>
        )}
        {failure === null && localMessage !== null && <FormAlert>{localMessage}</FormAlert>}

        <Section
          title="Workspace"
          description="The name people see inside the product, and the address Wend contacts about the account."
        >
          <TextField
            label="Workspace name"
            required
            value={form.name}
            error={errors.name}
            onChange={(value) => {
              update({ name: value });
            }}
            placeholder="Northwind Retail"
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Contact email"
              required
              type="email"
              description="Billing and operations. Not a login."
              value={form.email}
              error={errors.email}
              onChange={(value) => {
                update({ email: value });
              }}
            />
            <TextField
              label="Contact phone"
              required
              type="tel"
              description="E.164, e.g. +919812345678"
              value={form.phone}
              error={errors.phone}
              onChange={(value) => {
                update({ phone: value });
              }}
            />
          </div>
        </Section>

        <Section title="Organisation" description="The legal entity behind the workspace.">
          <TextField
            label="Legal name"
            required
            value={form.legalName}
            error={errors['organisation.legalName']}
            onChange={(value) => {
              update({ legalName: value });
            }}
            placeholder="Northwind Retail Private Limited"
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Website"
              type="url"
              value={form.website}
              error={errors['organisation.website']}
              onChange={(value) => {
                update({ website: value });
              }}
              placeholder="https://northwind.example"
            />
            <TextField
              label="Tax ID"
              description="GSTIN, VAT or equivalent."
              value={form.taxId}
              error={errors['organisation.taxId']}
              onChange={(value) => {
                update({ taxId: value });
              }}
            />
          </div>

          <TextField
            label="Address line 1"
            value={form.addressLine1}
            error={errors['organisation.addressLine1']}
            onChange={(value) => {
              update({ addressLine1: value });
            }}
          />
          <TextField
            label="Address line 2"
            value={form.addressLine2}
            error={errors['organisation.addressLine2']}
            onChange={(value) => {
              update({ addressLine2: value });
            }}
          />

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <TextField
              label="City"
              value={form.city}
              error={errors['organisation.city']}
              onChange={(value) => {
                update({ city: value });
              }}
            />
            <TextField
              label="State"
              value={form.state}
              error={errors['organisation.state']}
              onChange={(value) => {
                update({ state: value });
              }}
            />
            <TextField
              label="Postal code"
              value={form.postalCode}
              error={errors['organisation.postalCode']}
              onChange={(value) => {
                update({ postalCode: value });
              }}
            />
            <TextField
              label="Country"
              description="Two letters, e.g. IN"
              value={form.country}
              error={errors['organisation.country']}
              onChange={(value) => {
                update({ country: value });
              }}
            />
          </div>
        </Section>

        <Section
          title="Subscription"
          description="The licence this workspace runs on. Sign-in stops the moment it lapses."
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Subscription key"
              required
              description="Unique across all workspaces."
              value={form.subscriptionKey}
              error={errors.subscriptionKey}
              onChange={(value) => {
                update({ subscriptionKey: value });
              }}
            />
            <TextField
              label="Valid till"
              required
              type="date"
              min={todayInputValue()}
              description="Runs to the end of this day."
              value={form.validTill}
              error={errors.validTill}
              onChange={(value) => {
                update({ validTill: value });
              }}
            />
          </div>

          <FeaturePicker
            legend="Features"
            description="What this workspace can see once its owner signs in."
            value={form.features}
            onChange={setFeatures}
            error={errors.features}
          />
        </Section>

        <Section
          title="Owner"
          description="The first user. They receive a single-use link to choose their own password — no password is set here, and none is emailed."
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Owner name"
              required
              value={form.ownerName}
              error={errors['owner.name']}
              onChange={(value) => {
                update({ ownerName: value });
              }}
            />
            <TextField
              label="Owner email"
              required
              type="email"
              description="Their login, and where the set-password link goes."
              value={form.ownerEmail}
              error={errors['owner.email']}
              onChange={(value) => {
                update({ ownerEmail: value });
              }}
            />
          </div>

          <TextField
            label="Owner phone"
            type="tel"
            description="Optional. E.164, e.g. +919812345678"
            value={form.ownerPhone}
            error={errors['owner.phone']}
            onChange={(value) => {
              update({ ownerPhone: value });
            }}
          />
        </Section>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" loading={submitting}>
            Create workspace
          </Button>
          <Link to="/tenants" className={buttonVariants({ variant: 'ghost' })}>
            Cancel
          </Link>
        </div>
      </form>
    </AdminShell>
  );
}

/* -------------------------------------------------------------------------- */
/* success                                                                     */
/* -------------------------------------------------------------------------- */

function CreatedPanel({
  result,
  onCreateAnother,
}: {
  result: CreateTenantResponse;
  onCreateAnother: () => void;
}) {
  const { tenant, owner } = result;

  return (
    <Card>
      <CardHeader>
        <Badge tone="good" className="self-start">
          <CircleCheck aria-hidden="true" className="size-3.5" />
          Created
        </Badge>
        <CardTitle>{tenant.name}</CardTitle>
        <CardDescription>
          The workspace, its feature flags and its owner were created together.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {/*
          The whole point of this screen. It states that an email went out and
          when the link dies — it never shows, hints at, or implies a password,
          because none exists: the owner sets their own through the link.
        */}
        <Callout tone="good" title="Set-password email sent">
          <p className="text-text">
            A single-use link was emailed to{' '}
            <span className="font-mono text-[0.85rem] text-text">{owner.email}</span> at{' '}
            {formatDateTime(result.setPasswordEmailSentAt)}. It expires{' '}
            {formatDateTime(result.setPasswordTokenExpiresAt)} (
            {formatRelative(result.setPasswordTokenExpiresAt)}) and can be used once.
          </p>

          <p className="mt-2">
            Wend never emails a password and never creates one on someone&rsquo;s behalf.{' '}
            {owner.name} chooses their own when they open the link — it is not visible here, or to
            anyone else.
          </p>
        </Callout>

        <dl className="flex flex-col">
          <SummaryRow label="Workspace">{tenant.name}</SummaryRow>
          <SummaryRow label="Legal name">{tenant.organisation.legalName ?? '—'}</SummaryRow>
          <SummaryRow label="Contact">
            <span className="font-mono text-[0.82rem]">{tenant.email}</span> · {tenant.phone}
          </SummaryRow>
          <SummaryRow label="Subscription key">
            <span className="font-mono text-[0.82rem]">{tenant.subscriptionKey}</span>
          </SummaryRow>
          <SummaryRow label="Valid till">
            {formatDate(tenant.validTill)}{' '}
            <span className="text-muted">({formatRelative(tenant.validTill)})</span>
          </SummaryRow>
          <SummaryRow label="Features">
            <FeatureTags features={tenant.features} className="pt-1.5" />
          </SummaryRow>
          <SummaryRow label="Owner">
            {owner.name} · <span className="font-mono text-[0.82rem]">{owner.email}</span>
          </SummaryRow>
        </dl>
      </CardContent>

      <CardFooter className="flex-wrap">
        <Link to="/tenants" className={buttonVariants({ variant: 'secondary' })}>
          Back to tenants
        </Link>
        <Button type="button" variant="ghost" onClick={onCreateAnother}>
          Create another
        </Button>
      </CardFooter>
    </Card>
  );
}
